/**
 * PurgatoryPage.jsx
 *
 * The React Bouncer's landing zone.
 *
 * Two states:
 *   VILLAIN  — unauthenticated side-door entrant. Red alert. Full lockout.
 *   WAITER   — authenticated but not yet in authorized_users. Holding pattern.
 *
 * Character slot: swap CARICATURE_IMAGE_URL with your own image path.
 * The dance CSS animation is already wired — just drop the image in.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-config';

// ─── Character image ──────────────────────────────────────────────────────────
const CARICATURE_IMAGE_URL = '/images/superman.JPG';
const USE_CARICATURE_DANCE = true;
// ──────────────────────────────────────────────────────────────────────────────

const AUDIO_URL =
  'https://www.myinstants.com/media/sounds/ah-ah-ah-you-didnt-say-the-magic-word.mp3';

// Matrix rain characters
const MATRIX_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()_+-=[]{}|;:,.<>?アイウエオカキクケコ';

// ─────────────────────────────────────────────────────────────────────────────

function useMatrixRain(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const fontSize = 14;
    let columns = Math.floor(canvas.width / fontSize);
    let drops = Array(columns).fill(1);

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#ff0000';
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
        ctx.fillStyle = drops[i] * fontSize < canvas.height * 0.2 ? '#ff6666' : '#880000';
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 33);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef]);
}

// ─────────────────────────────────────────────────────────────────────────────

function GlitchText({ text, className = '' }) {
  return (
    <div className={`glitch-wrapper ${className}`} aria-label={text}>
      <span className="glitch" data-text={text}>{text}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function PurgatoryPage() {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const [visitorIP, setVisitorIP] = useState('Tracing...');
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'villain' | 'waiter'
  const [userEmail, setUserEmail] = useState('');
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  useMatrixRain(canvasRef);

  // IP fetch
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((d) => setVisitorIP(d.ip))
      .catch(() => setVisitorIP('Unknown Terminal'));
  }, []);

  // Auth watchdog — mirrors purgatory.html logic exactly
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserEmail(user.email || '');
        const snap = await getDoc(doc(db, 'users', user.uid));
        const isApproved = snap.exists() && snap.data().approved;
        if (isApproved) {
          // Already approved — they shouldn't be here. Send them home.
          window.location.replace('/');
          return;
        }
        setAuthState('waiter');
      } else {
        setAuthState('villain');
        // Log the intrusion
        if (visitorIP !== 'Tracing...') {
          addDoc(collection(db, 'security_logs'), {
            event: 'FULL_NEDRY_LOCKOUT',
            ip: visitorIP,
            timestamp: serverTimestamp(),
            path: window.location.pathname,
          }).catch(() => {});
        }
      }
    });

    // Kill the back button
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = () => window.history.go(1);

    return unsubscribe;
  }, [visitorIP]);

  const unlockAudio = useCallback(() => {
    if (!audioUnlocked && audioRef.current) {
      audioRef.current.play().catch(() => {});
      setAudioUnlocked(true);
    }
  }, [audioUnlocked]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.replace('/login');
    } catch {}
  };

  const isVillain = authState === 'villain';
  const isWaiter = authState === 'waiter';

  return (
    <div
      className={`purgatory-root ${isVillain ? 'villain-mode' : ''}`}
      onClick={unlockAudio}
      style={{ cursor: 'default' }}
    >
      {/* Matrix rain background */}
      <canvas ref={canvasRef} className="matrix-canvas" />

      {/* Scanline overlay */}
      <div className="scanlines" />

      {/* Main content */}
      <div className="purgatory-content">

        {/* Lock icon */}
        <div className="lock-icon">🔒</div>

        {/* Character — Nedry GIF or your caricature */}
        <div className="character-frame">
          <img
            src={CARICATURE_IMAGE_URL}
            alt="You didn't say the magic word."
            className={`character-img ${USE_CARICATURE_DANCE ? 'dance' : ''}`}
          />
        </div>

        {/* Access Denied headline */}
        <GlitchText text="ACCESS DENIED" className="access-denied-text" />

        {/* Status panel */}
        <div className={`status-panel ${isVillain ? 'villain-panel' : 'waiter-panel'}`}>
          {authState === 'loading' && (
            <p className="status-line">Initializing Security Trace...</p>
          )}

          {isVillain && (
            <>
              <p className="status-headline blink">⚠ INTRUDER DETECTED ⚠</p>
              <p className="status-line">
                Access attempt traced to IP: <strong className="ip-text">{visitorIP}</strong>
              </p>
              <p className="status-sub">Attempt logged to Security Grid</p>
              <p className="status-sub">Ah ah ah — you didn't say the magic word.</p>
            </>
          )}

          {isWaiter && (
            <>
              <p className="status-headline waiter-headline">Hold onto your butts.</p>
              <p className="status-line">
                <span className="email-text">{userEmail}</span> is pending admin approval.
              </p>
              <p className="status-sub">You're in the queue. Sit tight.</p>
              <p className="status-tiny">Terminal IP: {visitorIP}</p>
            </>
          )}
        </div>

        {/* Hidden logout */}
        <button className="logout-btn" onClick={handleLogout}>
          [ TERMINAL LOGOUT ]
        </button>
      </div>

      {/* Audio */}
      <audio ref={audioRef} loop>
        <source src={AUDIO_URL} type="audio/mpeg" />
      </audio>

      <style>{`
        .purgatory-root {
          position: fixed;
          inset: 0;
          background: #000;
          color: #ff0000;
          font-family: 'Courier New', Courier, monospace;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          user-select: none;
        }

        .villain-mode {
          animation: flash-red 0.5s infinite;
        }

        @keyframes flash-red {
          0%, 100% { background-color: #000; }
          50%       { background-color: #1a0000; }
        }

        /* Matrix rain */
        .matrix-canvas {
          position: absolute;
          inset: 0;
          z-index: 0;
          opacity: 0.35;
        }

        /* Scanlines */
        .scanlines {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.18) 2px,
            rgba(0,0,0,0.18) 4px
          );
          pointer-events: none;
        }

        /* Content stack */
        .purgatory-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
          padding: 2rem;
          max-width: 560px;
          width: 100%;
          text-align: center;
        }

        /* Lock */
        .lock-icon {
          font-size: 4rem;
          filter: drop-shadow(0 0 12px #ff0000);
          animation: wiggle 0.25s infinite;
        }
        @keyframes wiggle {
          0%   { transform: rotate(0deg); }
          25%  { transform: rotate(6deg); }
          75%  { transform: rotate(-6deg); }
          100% { transform: rotate(0deg); }
        }

        /* Character frame */
        .character-frame {
          border: 3px solid #ff0000;
          box-shadow: 0 0 30px #ff0000, inset 0 0 20px rgba(255,0,0,0.15);
          max-width: 380px;
          width: 100%;
          overflow: hidden;
          border-radius: 2px;
          animation: glow-pulse 2s ease-in-out infinite;
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px #ff0000, inset 0 0 15px rgba(255,0,0,0.1); }
          50%       { box-shadow: 0 0 50px #ff3300, inset 0 0 30px rgba(255,0,0,0.25); }
        }

        .character-img {
          width: 100%;
          display: block;
        }

        /* Dance animation for caricature still image */
        .character-img.dance {
          animation: dance 0.6s steps(1) infinite;
          transform-origin: bottom center;
        }
        @keyframes dance {
          0%   { transform: rotate(-4deg) translateY(0px);   }
          16%  { transform: rotate(0deg)  translateY(-6px);  }
          33%  { transform: rotate(4deg)  translateY(0px);   }
          50%  { transform: rotate(0deg)  translateY(-4px);  }
          66%  { transform: rotate(-3deg) translateY(0px);   }
          83%  { transform: rotate(2deg)  translateY(-3px);  }
          100% { transform: rotate(-4deg) translateY(0px);   }
        }

        /* Glitch text */
        .glitch-wrapper { position: relative; }
        .glitch {
          font-size: clamp(1.8rem, 6vw, 3rem);
          font-weight: 900;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #ff0000;
          text-shadow: 0 0 10px #ff0000;
          position: relative;
          display: inline-block;
        }
        .glitch::before,
        .glitch::after {
          content: attr(data-text);
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
        }
        .glitch::before {
          color: #0ff;
          animation: glitch-1 3s infinite linear;
          clip-path: polygon(0 0, 100% 0, 100% 35%, 0 35%);
          mix-blend-mode: screen;
        }
        .glitch::after {
          color: #f0f;
          animation: glitch-2 2s infinite linear;
          clip-path: polygon(0 65%, 100% 65%, 100% 100%, 0 100%);
          mix-blend-mode: screen;
        }
        @keyframes glitch-1 {
          0%, 90%, 100% { transform: translate(0); }
          92%            { transform: translate(-3px, 1px); }
          94%            { transform: translate(3px, -1px); }
          96%            { transform: translate(-2px, 0); }
        }
        @keyframes glitch-2 {
          0%, 85%, 100% { transform: translate(0); }
          87%            { transform: translate(3px, 2px); }
          90%            { transform: translate(-3px, -1px); }
          93%            { transform: translate(2px, 0); }
        }

        /* Status panel */
        .status-panel {
          width: 100%;
          padding: 0.9rem 1.2rem;
          border: 1px solid #ff0000;
          background: rgba(136, 0, 0, 0.12);
          font-size: 0.85rem;
          line-height: 1.7;
        }
        .waiter-panel {
          border-color: #ff8800;
          background: rgba(136, 60, 0, 0.12);
          color: #ff8800;
        }

        .status-headline {
          font-size: 1rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.4rem;
        }
        .waiter-headline { color: #ff8800; }
        .blink { animation: blink 0.8s step-end infinite; }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.2; }
        }

        .status-line { color: #ff4444; margin: 0; }
        .waiter-panel .status-line { color: #ffaa44; }
        .ip-text   { color: #ff8800; font-weight: bold; }
        .email-text { color: #ffcc00; font-weight: bold; }
        .status-sub  { font-size: 0.75rem; color: #aa2222; margin: 0; }
        .status-tiny { font-size: 0.65rem; color: #663300; margin: 0; opacity: 0.6; }

        /* Logout */
        .logout-btn {
          margin-top: 0.5rem;
          background: none;
          border: none;
          color: #1a0000;
          font-family: inherit;
          font-size: 0.7rem;
          cursor: pointer;
          opacity: 0.08;
          transition: opacity 0.3s;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }
        .logout-btn:hover { opacity: 1; color: #ff4400; }
      `}</style>
    </div>
  );
}
